/**
 * Enquête n°24 — Adapté de l'Étude de Cas CCS n°21
 * (Réglementation des équipements d'éclairage et de signalisation.)
 */

import { Lightbulb, ShieldCheck, AlertTriangle, Users, Quote } from 'lucide-react';

export const eclairageSignalisationEnquete = {
  slug: 'eclairage-signalisation-moto',
  themeCode: 'V',
  issueNumber: 24,
  totalIssues: 24,
  series: 'enquetes',
  category: 'Enquête réglementation véhicule',
  shortTitle: 'Éclairage « obligatoire »',
  title: 'Éclairage moto : ce que dit vraiment le code (et ce que les forces de l\'ordre contrôlent)',
  kicker: 'Enquête n°24 · Série « Témoignages & Sécurité »',
  subtitle:
    "Feux de croisement, feux de route, feux de brouillard, clignotants, feu stop, plaque d'immatriculation. Articles R313-3 à R313-7 : ce qui est obligatoire, ce qui est toléré, ce qui te coûte 135 €.",
  heroImage: '/img/magazines/route.jpg',
  heroImageAlt: 'Panneaux de signalisation routière au bord d\'une route — éclairage et signalisation moto',
  readTimeMin: 6,
  seoDescription:
    'Enquête sur la réglementation éclairage moto : feux de croisement, route, brouillard, clignotants, feu stop. Articles R313-3 à R313-7 du Code de la route, amendes encourues, contrôles fréquents.',
  seoKeywords:
    'éclairage moto, R313-3, R313-7, feux moto, feux de brouillard, clignotants moto, plaque immatriculation, contrôle moto',

  edito: {
    title: 'L’éclairage qui te rend visible (et légal)',
    body: 'Un motard sur trois roule avec un éclairage non conforme et l’ignore. <strong>Ampoule LED non homologuée</strong>, <strong>clignotants trafiqués</strong>, <strong>plaque sans éclairage</strong> : 135 € chacun, parfois immobilisation du véhicule. Cette enquête fait le tri entre ce qui est légal, ce qui est toléré, et ce qui te coûte un week-end de rouler.',
    signedBy: 'La rédaction Code 2 Roues',
  },

  sections: [
    {
      type: 'lesson',
      icon: ShieldCheck,
      partLabel: 'Chapitre 1',
      title: 'Les feux obligatoires (R313-3 à R313-5)',
      paragraphs: [
        '<strong>Feu de croisement (FEUX BLANCS) obligatoire en marche</strong> 24/24 sur toute moto > 50 cm³ (depuis 1991 art. R313-3). Pour les cyclomoteurs (≤ 50 cm³), obligatoire la nuit et conseillé jour.',
        '<strong>Feu de route (« pleins phares »)</strong> : obligatoire hors agglo la nuit si pas d’autre véhicule. Coupure obligatoire en croisement de véhicule ou en suivant un véhicule à 150 m.',
        '<strong>Feu rouge arrière (POSITION)</strong> + <strong>feu stop</strong> + <strong>clignotants</strong> + <strong>éclairage de plaque</strong> : tous obligatoires en état de marche.',
        '<strong>Catadioptre arrière</strong> rouge non triangulaire (R313-20) : obligatoire mais souvent oublié.',
      ],
      keyPoints: [
        'Croisement 24/24 sur moto > 50 cm³',
        'Route hors agglo si seul',
        'Plaque doit être éclairée',
        'Catadioptre arrière rouge',
      ],
},
    {
      type: 'lesson',
      icon: ShieldCheck,
      partLabel: 'Chapitre 2',
      title: 'Les feux de brouillard (R313-6 et R313-7)',
      paragraphs: [
        '<strong>Feux de brouillard AVANT</strong> (optionnel) : peuvent être allumés en cas de brouillard, neige, pluie très forte. Couleur blanche ou jaune autorisée.',
        "<strong>Feux de brouillard ARRIÈRE</strong> (R313-7) : OBLIGATOIRES à utiliser si visibilité < 50 m (brouillard, neige forte, pluie très dense). INTERDITS hors de ces conditions (éblouissent le véhicule suiveur).",
        '<strong>Éclairage hivernal</strong> : pas d’obligation spécifique au froid mais les ampoules halogènes sont sensibles à la condensation, vérifie ton phare quand tu sors de garage.',
      ],
      keyPoints: [
        'Brouillard arrière : visibilité < 50 m',
        'Brouillard arrière INTERDIT autrement',
        'Brouillard avant : toléré',
      ],
},
    {
      type: 'didyouknow',
      icon: Lightbulb,
      title: "Les LED 'tuning' : 90 % sont ILLEGALES",
      body: 'Une ampoule LED de remplacement directe d’une H7 halogène n’est <strong>légale en France</strong> que si elle est <strong>homologuée ECE R128</strong> (marquage « E + chiffre » sur le culot). 90 % des LED vendues en grande surface ou Amazon ne le sont pas. Conséquences : 135 € d’amende + immobilisation potentielle + assurance qui peut refuser la garantie en cas d’accident de nuit. Réflexe : achète chez le concessionnaire ou vérifie le marquage ECE R128.',
    },
    {
      type: 'lesson',
      icon: AlertTriangle,
      partLabel: 'Chapitre 3',
      title: 'Les 4 contraventions les plus fréquentes',
      paragraphs: [
        '<strong>1. Plaque arrière sans éclairage</strong> ou avec éclairage HS : <strong>135 €</strong> (R317-7).',
        '<strong>2. Clignotants non homologués</strong> ou ampoules trafiquées (autres couleurs que orange/ambre) : <strong>135 €</strong> + immobilisation.',
        '<strong>3. Phare avant en panne</strong> (1 ampoule HS): <strong>135 €</strong> (R313-3).',
        '<strong>4. Feu de brouillard arrière allumé sans brouillard</strong> : <strong>135 €</strong> (R313-7).',
      ],
      keyPoints: [
        'Plaque non éclairée : 135 €',
        'Clignos non homologués : 135 €',
        'Phare HS : 135 €',
        'Brouillard arrière abusif : 135 €',
      ],
},
    {
      type: 'didyouknow',
      icon: ShieldCheck,
      title: "Posture ECSR — Le contrôle routier, c'est ton allié",
      body: "Sur la route, les <strong>forces de l'ordre</strong> (gendarmerie nationale, police nationale, police municipale selon le territoire) ne sont pas tes adversaires : ce sont les garants du <strong>cadre légal qui te protège AUSSI</strong>. Quand un gendarme te contrôle l'éclairage, il vérifie que tu es visible la nuit — donc il participe à ce qui te garde en vie. <strong>Réflexe ECSR</strong> : à l'arrêt, coupe le moteur, retire ton casque si possible, place les mains visibles sur le guidon, présente calmement permis + carte grise + assurance. Un échange courtois fait souvent gagner une indulgence sur les détails. <strong>L'irrespect des forces de l'ordre n'est jamais une stratégie gagnante</strong> — ni légalement (outrage R434-2 : 7 500 € + 6 mois), ni statistiquement (le contrôle s'éternise et se durcit).",
    },
    {
      type: 'tip',
      icon: Users,
      title: 'Tu envisages de personnaliser ?',
      body: "Antérieur à toute modification : vérifie le marquage <strong>ECE R128</strong> (ampoules LED) ou <strong>ECE R6</strong> (clignotants). Sans ça, ton assurance peut se retirer en cas d'accident de nuit. Pas de jeu à faire là-dessus.",
    }
  ],
};

export default eclairageSignalisationEnquete;
